import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import CandlestickChart, { CHART_TYPES } from './CandlestickChart';
import IndicatorSelector from './IndicatorSelector';
import { aggregateCandles, getAvailableTimeframes } from '../utils/timeframeAggregator';
import { computeIndicatorsForCandles } from '../hooks/useIndicators';
import { getIndicator } from '../indicators/registry';

export default function MultiTimeframeView({
  visibleCandles, baseTimeframe, selectedTfs, onToggleTf, onReorderTfs,
  primaryChart, indicatorSelector,
  showVolume, onToggleVolume,
  chartType, onChartTypeChange,
  showNews, onToggleNews,
  correlationPairs = {}, // { dsId: { instrument, timeframe, candles } }
  onRemoveCorrelation,
  correlationLoading = false,
  onCrosshairSync: onPrimaryCrosshairSync,
  syncCrosshairTime: primarySyncTime,
}) {
  const availableTfs = useMemo(() => getAvailableTimeframes(baseTimeframe), [baseTimeframe]);
  const containerRef = useRef(null);

  // Crosshair sync state: when one chart moves, others follow
  const [syncTime, setSyncTime] = useState(null);
  const syncSourceRef = useRef(null); // which key triggered the sync

  const handleCrosshairSync = useCallback((key, time) => {
    syncSourceRef.current = key;
    setSyncTime(time);
    // Also notify primary chart (in ReplayPage) when secondary moves
    if (key !== '__primary__' && onPrimaryCrosshairSync) {
      onPrimaryCrosshairSync(time);
    }
  }, [onPrimaryCrosshairSync]);

  // Per-TF chart type
  const [tfChartTypes, setTfChartTypes] = useState({});
  const getTfChartType = (key) => tfChartTypes[key] || chartType || 'candles';
  const setTfChartType = useCallback((key, type) => {
    setTfChartTypes((prev) => ({ ...prev, [key]: type }));
  }, []);

  // Per-TF independent indicators
  const [tfIndicatorConfigs, setTfIndicatorConfigs] = useState({}); // { tf: [activeIndicator] }
  const getTfIndicators = (key) => tfIndicatorConfigs[key] || [];
  const addTfIndicator = useCallback((key, config) => {
    setTfIndicatorConfigs((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []), config],
    }));
  }, []);
  const removeTfIndicator = useCallback((key, instanceId) => {
    setTfIndicatorConfigs((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((a) => a.instanceId !== instanceId),
    }));
  }, []);
  const updateTfIndicatorParams = useCallback((key, instanceId, newParams) => {
    setTfIndicatorConfigs((prev) => ({
      ...prev,
      [key]: (prev[key] || []).map((a) =>
        a.instanceId === instanceId ? { ...a, params: { ...a.params, ...newParams } } : a
      ),
    }));
  }, []);

  // Card positions: { [key]: { x, y, w, h } }
  const [positions, setPositions] = useState({});
  const [poppedOut, setPoppedOut] = useState({});
  const [dragging, setDragging] = useState(null); // { key, startX, startY, origX, origY }
  const popoutWindowsRef = useRef({});
  const channelRef = useRef(null);

  // All visible card keys (primary first, then TFs not popped out)
  const primaryPopped = poppedOut.__primary__;
  const corrKeys = useMemo(() =>
    Object.entries(correlationPairs)
      .filter(([_, d]) => d.candles.length > 0)
      .map(([dsId]) => `corr_${dsId}`),
    [correlationPairs]
  );

  const cardKeys = useMemo(() => {
    const keys = [];
    if (!primaryPopped) keys.push('__primary__');
    selectedTfs.forEach((tf) => { if (!poppedOut[tf]) keys.push(tf); });
    corrKeys.forEach((ck) => { if (!poppedOut[ck]) keys.push(ck); });
    return keys;
  }, [selectedTfs, poppedOut, primaryPopped, corrKeys]);

  // Track card count to detect when to re-tile
  const prevCountRef = useRef(0);

  // Compute tiled layout for all cards
  const computeLayout = useCallback(() => {
    const container = containerRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    const totalW = rect.width;
    const totalH = rect.height;
    const count = cardKeys.length;
    if (count === 0) return {};

    const gap = 4;
    const next = {};

    if (count === 1) {
      next[cardKeys[0]] = { x: 0, y: 0, w: totalW, h: totalH };
    } else {
      const hasPrimary = cardKeys[0] === '__primary__';
      if (hasPrimary) {
        // Primary 60% left, TFs stacked right
        const primaryW = Math.floor(totalW * 0.6);
        const tfW = totalW - primaryW - gap;
        next.__primary__ = { x: 0, y: 0, w: primaryW, h: totalH };
        const tfKeys = cardKeys.slice(1);
        const tfCount = tfKeys.length;
        const tfH = Math.floor((totalH - (tfCount - 1) * gap) / tfCount);
        tfKeys.forEach((tf, i) => {
          next[tf] = { x: primaryW + gap, y: i * (tfH + gap), w: tfW, h: tfH };
        });
      } else {
        // No primary: grid
        const cols = count <= 2 ? count : 2;
        const rows = Math.ceil(count / cols);
        const cellW = Math.floor((totalW - (cols - 1) * gap) / cols);
        const cellH = Math.floor((totalH - (rows - 1) * gap) / rows);
        cardKeys.forEach((k, i) => {
          const col = i % cols;
          const row = Math.floor(i / cols);
          next[k] = { x: col * (cellW + gap), y: row * (cellH + gap), w: cellW, h: cellH };
        });
      }
    }
    return next;
  }, [cardKeys]);

  // Re-tile whenever the number of cards changes
  useEffect(() => {
    if (cardKeys.length !== prevCountRef.current) {
      prevCountRef.current = cardKeys.length;
      const layout = computeLayout();
      if (layout) setPositions(layout);
    }
  }, [cardKeys, computeLayout]);

  // Drag to move
  const handleMouseDown = useCallback((e, key) => {
    if (e.target.closest('.chart-card-actions') || e.target.closest('.indicator-selector')) return;
    e.preventDefault();
    const pos = positions[key];
    if (!pos) return;
    setDragging({ key, startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y });
  }, [positions]);

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      const dx = e.clientX - dragging.startX;
      const dy = e.clientY - dragging.startY;
      setPositions((prev) => ({
        ...prev,
        [dragging.key]: { ...prev[dragging.key], x: dragging.origX + dx, y: dragging.origY + dy },
      }));
    };
    const handleUp = () => setDragging(null);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [dragging]);

  // BroadcastChannel
  useEffect(() => {
    const channel = new BroadcastChannel('tradrin-tf-sync');
    channelRef.current = channel;
    channel.onmessage = (e) => {
      if (e.data.type === 'popout-closed') {
        const key = e.data.tf === baseTimeframe ? '__primary__' : e.data.tf;
        setPoppedOut((prev) => ({ ...prev, [key]: false }));
        delete popoutWindowsRef.current[key];
        // Force re-layout for returning card
        setPositions((prev) => { const n = { ...prev }; delete n[key]; return n; });
      }
    };
    return () => {
      channel.close();
      Object.values(popoutWindowsRef.current).forEach((w) => { try { w.close(); } catch {} });
    };
  }, [baseTimeframe]);

  const aggregatedData = useMemo(() => {
    const result = {};
    for (const tf of selectedTfs) result[tf] = aggregateCandles(visibleCandles, tf);
    return result;
  }, [visibleCandles, selectedTfs]);

  // Compute indicators for each secondary TF from their own config
  const tfIndicators = useMemo(() => {
    const result = {};
    for (const tf of selectedTfs) {
      const config = tfIndicatorConfigs[tf];
      if (!config || config.length === 0) continue;
      const candles = aggregatedData[tf];
      if (candles && candles.length > 0) {
        result[tf] = computeIndicatorsForCandles(config, candles);
      }
    }
    // Correlation pair indicators
    for (const ck of corrKeys) {
      const config = tfIndicatorConfigs[ck];
      if (!config || config.length === 0) continue;
      const dsId = ck.replace('corr_', '');
      const candles = correlationPairs[dsId]?.candles;
      if (candles && candles.length > 0) {
        result[ck] = computeIndicatorsForCandles(config, candles);
      }
    }
    return result;
  }, [tfIndicatorConfigs, aggregatedData, selectedTfs, corrKeys, correlationPairs]);

  useEffect(() => {
    if (!channelRef.current) return;
    for (const tf of selectedTfs) {
      if (poppedOut[tf] && aggregatedData[tf])
        channelRef.current.postMessage({ tf, candles: aggregatedData[tf] });
    }
    if (poppedOut.__primary__ && visibleCandles.length > 0)
      channelRef.current.postMessage({ tf: baseTimeframe, candles: visibleCandles });
  }, [aggregatedData, selectedTfs, poppedOut, visibleCandles, baseTimeframe]);

  const handlePopout = useCallback((key) => {
    const actualTf = key === '__primary__' ? baseTimeframe : key;
    const w = window.open(
      `/popout?tf=${encodeURIComponent(actualTf)}`,
      `tradrin-tf-${key}`, 'width=900,height=550,menubar=no,toolbar=no,location=no,status=no'
    );
    if (w) {
      popoutWindowsRef.current[key] = w;
      setPoppedOut((prev) => ({ ...prev, [key]: true }));
      setTimeout(() => {
        if (channelRef.current) {
          const candles = key === '__primary__' ? visibleCandles : aggregatedData[key];
          if (candles) channelRef.current.postMessage({ tf: actualTf, candles });
        }
      }, 500);
      const check = setInterval(() => {
        if (w.closed) {
          clearInterval(check);
          setPoppedOut((prev) => ({ ...prev, [key]: false }));
          delete popoutWindowsRef.current[key];
          setPositions((prev) => { const n = { ...prev }; delete n[key]; return n; });
        }
      }, 1000);
    }
  }, [aggregatedData, visibleCandles, baseTimeframe]);

  const handlePopin = useCallback((key) => {
    const w = popoutWindowsRef.current[key];
    if (w) { try { w.close(); } catch {} }
    setPoppedOut((prev) => ({ ...prev, [key]: false }));
    delete popoutWindowsRef.current[key];
    setPositions((prev) => { const n = { ...prev }; delete n[key]; return n; });
  }, []);

  // Render a single card
  const renderCard = (key) => {
    const pos = positions[key] || { x: 0, y: 0, w: 400, h: 300 };
    const isPrimary = key === '__primary__';
    const isCorrelation = key.startsWith('corr_');
    const corrDsId = isCorrelation ? key.replace('corr_', '') : null;
    const corrData = isCorrelation ? correlationPairs[corrDsId] : null;

    return (
      <div
        key={key}
        className={`chart-card ${isPrimary ? 'chart-card-primary' : ''} ${isCorrelation ? 'chart-card-corr' : ''}`}
        style={{ left: pos.x, top: pos.y, width: pos.w, height: pos.h }}
      >
        <div className="chart-card-header" onMouseDown={(e) => handleMouseDown(e, key)}>
          {!isPrimary && <span className="chart-card-grip">&#x2630;</span>}
          <span className="chart-card-label">
            {isPrimary ? `${baseTimeframe} (main)` : isCorrelation && corrData ? `${corrData.instrument} (corr)` : key}
          </span>
          {isPrimary && indicatorSelector}
          {!isPrimary && (
            <IndicatorSelector
              activeIndicators={getTfIndicators(key)}
              onAdd={(config) => addTfIndicator(key, config)}
              onRemove={(instanceId) => removeTfIndicator(key, instanceId)}
              onUpdate={(instanceId, params) => updateTfIndicatorParams(key, instanceId, params)}
            />
          )}
          {!isPrimary && !isCorrelation && (
            <div className="chart-type-selector chart-type-selector--sm">
              {CHART_TYPES.map((ct) => (
                <button
                  key={ct.id}
                  className={`chart-type-btn ${getTfChartType(key) === ct.id ? 'active' : ''}`}
                  onClick={() => setTfChartType(key, ct.id)}
                  title={ct.label}
                >
                  {ct.icon}
                </button>
              ))}
            </div>
          )}
          {isPrimary && onChartTypeChange && (
            <div className="chart-type-selector">
              {CHART_TYPES.map((ct) => (
                <button
                  key={ct.id}
                  className={`chart-type-btn ${chartType === ct.id ? 'active' : ''}`}
                  onClick={() => onChartTypeChange(ct.id)}
                  title={ct.label}
                >
                  {ct.icon}
                </button>
              ))}
            </div>
          )}
          {isPrimary && onToggleVolume && (
            <button
              className={`vol-toggle-btn ${showVolume ? 'active' : ''}`}
              onClick={onToggleVolume}
              title={showVolume ? 'Hide volume' : 'Show volume'}
            >
              Vol
            </button>
          )}
          {isPrimary && onToggleNews && (
            <button
              className={`vol-toggle-btn ${showNews ? 'active' : ''}`}
              onClick={onToggleNews}
              title={showNews ? 'Hide news' : 'Show news'}
            >
              News
            </button>
          )}
          <div className="chart-card-actions">
            <button className="multi-tf-popout-btn" onClick={() => handlePopout(key)} title="Detach">&#x29C9;</button>
            {!isPrimary && !isCorrelation && (
              <button className="multi-tf-close-btn" onClick={() => onToggleTf(key)} title="Remove">&times;</button>
            )}
            {isCorrelation && onRemoveCorrelation && (
              <button className="multi-tf-close-btn" onClick={() => onRemoveCorrelation(parseInt(corrDsId))} title="Remove">&times;</button>
            )}
          </div>
        </div>
        <div className="chart-card-body">
          {isPrimary ? primaryChart : isCorrelation && corrData ? (
            <CandlestickChart
              candles={corrData.candles} height={200} showVolume={false}
              chartType={getTfChartType(key)}
              overlayIndicators={tfIndicators[key]?.overlays || []}
              panelIndicators={tfIndicators[key]?.panels || []}
              onCrosshairSync={(time) => handleCrosshairSync(key, time)}
              syncCrosshairTime={syncSourceRef.current !== key ? syncTime : undefined}
            />
          ) : (
            <CandlestickChart
              candles={aggregatedData[key] || []} height={200} showVolume={false}
              chartType={getTfChartType(key)}
              overlayIndicators={tfIndicators[key]?.overlays || []}
              panelIndicators={tfIndicators[key]?.panels || []}
              onCrosshairSync={(time) => handleCrosshairSync(key, time)}
              syncCrosshairTime={syncSourceRef.current !== key ? syncTime : undefined}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="chart-grid" ref={containerRef}>
      {cardKeys.map(renderCard)}

      {/* Loading placeholder for correlation pairs */}
      {correlationLoading && (
        <div className="corr-loading-card">
          <div className="corr-loading-spinner" />
          <span>Loading correlation pair</span>
          <span>Downloading candle data, this may take a moment...</span>
        </div>
      )}

      {/* Popped-out badges */}
      {Object.entries(poppedOut).some(([, v]) => v) && (
        <div className="chart-popout-bar">
          {primaryPopped && (
            <span className="multi-tf-popout-badge">
              {baseTimeframe} (main) &#x29C9;
              <button className="multi-tf-popout-popin" onClick={() => handlePopin('__primary__')}>&#x21A9;</button>
            </span>
          )}
          {selectedTfs.filter((tf) => poppedOut[tf]).map((tf) => (
            <span key={tf} className="multi-tf-popout-badge">
              {tf} &#x29C9;
              <button className="multi-tf-popout-popin" onClick={() => handlePopin(tf)}>&#x21A9;</button>
              <button className="multi-tf-popout-remove" onClick={() => { handlePopin(tf); onToggleTf(tf); }}>&times;</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function TimeframeSelector({ baseTimeframe, selectedTfs, onToggleTf }) {
  const availableTfs = useMemo(() => getAvailableTimeframes(baseTimeframe), [baseTimeframe]);
  if (availableTfs.length === 0) return null;
  return (
    <div className="tf-selector">
      <span className="tf-base-badge">{baseTimeframe}</span>
      {availableTfs.map((tf) => (
        <button key={tf} className={`tf-btn ${selectedTfs.includes(tf) ? 'active' : ''}`}
          onClick={() => onToggleTf(tf)}>{tf}</button>
      ))}
    </div>
  );
}
